import { pinia } from '@/main'
import { defineStore } from 'pinia'
import { requestExportForSurveyAPI } from '@/services/results-service'
import { useSurveysStore } from '@/stores/surveys-store'
import { useStatesStore } from '@/stores/states-store'
import { oldSurveysWithinTimePeriod } from './helpers/trend-store-helper'

const triggerFileDownload = (blob, defaultFileName) => {
  // Fallback to using an invisible <a> element
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = defaultFileName
  // Append to the document to make it part of the DOM
  document.body.appendChild(a)
  a.click()
  // Clean up
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const isHttpsUrl = (str) => {
  try {
    const url = new URL(str)
    return url.protocol === 'https:'
  } catch (err) {
    return false
  }
}

export const useExportStore = defineStore('export-store', {
  state: () => ({
    error: {},
    loading: {},
    exportRequestForSurveyLoading: false,
    exportRequestForSurveySuccess: false,
    exportRequestForSurveyError: null,
    exportRequestForSurveyData: null
  }),
  actions: {
    clear() {
      this.error = {}
      this.loading = {}
      this.insightsTeam = {}
      this.insightsLeadership = {}
    },
    async requestExportForSurveyAction({
      groupName,
      groupTitle,
      exportMethod,
      surveyName,
      sortedGroupNames,
      exportFormat,
      email = null,
      customLogo = null, // base64 encoded string of image
      includeSensitiveQuestions = false,
      shouldExportMultipleGroupsForEntireOrganization = false,
      includeHistory = false,
      theme = null,
      participationSettings = null,
      resultViewForUser,
      filter
    }) {
      this.exportRequestForSurveyData = null
      this.exportRequestForSurveyError = null
      this.exportRequestForSurveyLoading = true

      const statesStore = useStatesStore(pinia)
      const surveysStore = useSurveysStore(pinia)

      const options = {
        sortedGroupNames,
        includeSensitive: includeSensitiveQuestions
      }

      let surveyNames = null
      if (groupName !== 'all') {
        if (includeHistory) {
          // only support for history when exporting for single grop
          // amd other settings too
          surveyNames = oldSurveysWithinTimePeriod({
            surveyDetails: surveysStore.getSurveyDetails,
            selectedTimePeriod: statesStore.getSelectedTimePeriod
          })
        }

        options.formatConfig = {
          groupComparisonSettings:
            surveysStore.getSurveyDetails.details.resultViews
              .groupComparisonSettings,
          resultViewRows: resultViewForUser.rows
        }

        if (participationSettings) {
          options.formatConfig.participationSettings = participationSettings
        }

        if (customLogo) {
          options.formatConfig.customLogo = customLogo
        }

        if (theme) {
          options.formatConfig.theme = theme
        }

        if (statesStore.surveyIsAggregate) {
          options.isAggregate = statesStore.surveyIsAggregate
        }
      } else if (!shouldExportMultipleGroupsForEntireOrganization) {
        // when requesting export for all groups it's not needed
        // to pass a groupFilter
        options.groupFilter = sortedGroupNames
      }

      try {
        if (exportMethod === 'email') {
          // disabled for now
          await requestExportForSurveyAPI({
            // just fire away
            groupTitle,
            groupName,
            surveyName,
            surveyNames,
            email,
            type: exportMethod,
            format: exportFormat,
            filter,
            options
          })
        } else if (exportMethod === 'download') {
          const result = await requestExportForSurveyAPI({
            groupTitle,
            groupName,
            surveyName,
            surveyNames,
            type: exportMethod,
            format: exportFormat,
            filter,
            options
          })
          if (result.result) {
            const res = JSON.parse(result.result)
            const fileName = Object.keys(res.export)[0]
            const exportData = res.export && res.export[fileName]
            if (exportData && isHttpsUrl(exportData)) {
              const link = document.createElement('a')
              link.href = `${exportData}`
              link.download = `${fileName}-${surveyName}.${exportFormat}`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            } else if (exportData && exportData.formattedResults) {
              const buffer = exportData.formattedResults
              if (buffer.type === 'Buffer' && Array.isArray(buffer.data)) {
                const byteArray = new Uint8Array(buffer.data)
                const blob = new Blob([byteArray], {
                  type: 'application/octet-stream'
                })
                triggerFileDownload(
                  blob,
                  `${fileName}-${surveyName}.${exportFormat}`
                )
              } else {
                console.error('Unexpected buffer format:', buffer)
                this.exportRequestForSurveyError = new Error('Bad data')
              }
            } else {
              this.exportRequestForSurveyError = new Error(
                'Export data not found in the response.'
              )
              console.error('Export data not found in the response.')
            }
            // this.exportRequestForSurveyData = res
          }
        }
      } catch (error) {
        this.exportRequestForSurveyError = error
        throw error
      } finally {
        this.exportRequestForSurveyLoading = false
      }
    }
  }
})
